/**
 * Read an answer aloud (UC-A6).
 *
 * ## Two engines, the browser first
 *
 * The workstation's own `speechSynthesis` is tried first: it is in every browser an employee
 * already has, it costs the deployment no GPU, no container and no model download, and for English
 * it is universally present. The server engine (VODER, Piper, any OpenAI-compatible TTS) is the
 * fallback for a language the machine has no voice for — in practice Arabic on a stripped Windows
 * image. See `shared/src/speech/browserSpeech.ts` for what the API makes awkward, and
 * `docs/adr/0008-voice-input-and-read-aloud.md` for why this ordering.
 *
 * The control is therefore present far more often than it used to be: a deployment with no speech
 * service at all still reads English answers aloud.
 *
 * ## Why it reads the raw answer rather than the screen
 *
 * Same reason `CopyButton` does: citation markers are rendered as chips whose DOM text is a bare
 * number, so reading the screen would speak "the fee is 7 LYD one" and an employee would hear a
 * figure that is not in the document. The markers are stripped for speech instead — a spoken
 * "bracket one" is noise, and the sources are on screen for anyone who wants them.
 *
 * ## Long answers are read in pieces, and the pieces are sentences
 *
 * The deployment caps how much text it will synthesise in one call, because a single large request
 * occupies the GPU the assistant answers with. So a long answer is split — on sentence boundaries
 * in both scripts, never mid-word — and played in order, fetching the next piece while the current
 * one plays. Splitting on a character count would cut a fee in half.
 *
 * ## Pressing again stops, and leaving the page stops
 *
 * An answer still being read after the employee has moved on is the failure that makes people stop
 * using a feature like this. The audio element is torn down on unmount and its object URL revoked,
 * because a leaked blob URL is a copy of a bank answer held in the browser for the tab's lifetime.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  ApiError,
  SpeakerIcon,
  SpeakerOffIcon,
  cx,
  speak,
  speakInBrowser,
  useBrowserVoice,
  useSpeechStatus,
  type BrowserSpeechHandle,
} from "@nab/shared";

import { ACTION_BUTTON } from "./actionButton";

export interface SpeakButtonProps {
  /** The answer as the model wrote it, markers and all. */
  text: string;
  /** The answer's language, not the interface's (golden rule 7). */
  language: string;
  className?: string;
}

type State = "idle" | "loading" | "playing" | "failed";

/**
 * Sentence ends in both scripts.
 *
 * `؟` and `۔` are the Arabic question mark and full stop; a split that only knew `.?!` would read
 * an entire Arabic answer as one sentence and then cut it at a character count.
 */
const SENTENCE_END = /(?<=[.!?؟۔])\s+/;

/**
 * How much text goes into one browser utterance.
 *
 * Chrome abandons a long utterance partway through — somewhere around fifteen seconds of speech —
 * and does it silently, so the answer simply stops being read. Short pieces, chained, avoid it.
 * The server path uses the deployment's own limit instead, which exists for a different reason.
 */
const BROWSER_CHUNK_LIMIT = 200;

/** Split into pieces no longer than `limit`, on sentence boundaries where possible. */
export function intoSpeakableChunks(text: string, limit: number): string[] {
  // The space *before* the marker goes with it. "7 LYD [1]." otherwise becomes "7 LYD ." and is
  // read with a pause where the citation was, which sounds like a hesitation over the figure.
  const clean = text
    .replace(/\s*\[\d+\]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  if (!clean) return [];
  if (clean.length <= limit) return [clean];

  const chunks: string[] = [];
  let current = "";

  for (const sentence of clean.split(SENTENCE_END)) {
    if (current && current.length + sentence.length + 1 > limit) {
      chunks.push(current);
      current = "";
    }
    if (sentence.length > limit) {
      // One sentence longer than the whole budget. Split it on spaces rather than mid-word: a
      // word cut in half is read as two words that are not in the document.
      if (current) {
        chunks.push(current);
        current = "";
      }
      let piece = "";
      for (const word of sentence.split(" ")) {
        if (piece.length + word.length + 1 > limit) {
          chunks.push(piece);
          piece = "";
        }
        piece = piece ? `${piece} ${word}` : word;
      }
      current = piece;
      continue;
    }
    current = current ? `${current} ${sentence}` : sentence;
  }

  if (current) chunks.push(current);
  return chunks;
}

export function SpeakButton({
  text,
  language,
  className,
}: SpeakButtonProps): JSX.Element | null {
  const { t } = useTranslation("app");
  const status = useSpeechStatus();
  const voice = useBrowserVoice(language);
  const [state, setState] = useState<State>("idle");

  const audio = useRef<HTMLAudioElement | null>(null);
  const browser = useRef<BrowserSpeechHandle | null>(null);
  const url = useRef<string | null>(null);
  // Set when the employee presses stop, so a fetch already in flight does not start playing after
  // they have asked for silence.
  const stopped = useRef(false);

  const release = useCallback(() => {
    browser.current?.cancel();
    browser.current = null;
    audio.current?.pause();
    audio.current = null;
    if (url.current) {
      // Revoked rather than left: a blob URL is a copy of a bank answer held for the tab's life.
      URL.revokeObjectURL(url.current);
      url.current = null;
    }
  }, []);

  useEffect(() => release, [release]);

  /** Read it on this machine. Resolves when the answer has been read, rejects if the engine fails. */
  const playInBrowser = useCallback(
    (chosen: SpeechSynthesisVoice) =>
      new Promise<void>((resolve, reject) => {
        const pieces = intoSpeakableChunks(text, BROWSER_CHUNK_LIMIT);
        if (pieces.length === 0) {
          resolve();
          return;
        }
        setState("playing");
        browser.current = speakInBrowser({
          pieces,
          voice: chosen,
          language,
          onDone: resolve,
          onError: (reason) => reject(new Error(reason)),
        });
      }),
    [text, language],
  );

  const play = useCallback(async () => {
    stopped.current = false;
    setState("loading");

    if (voice) {
      try {
        await playInBrowser(voice);
      } catch (caught) {
        if (!stopped.current) {
          setState("failed");
          console.warn("read-aloud failed in the browser:", caught);
          window.setTimeout(() => setState("idle"), 3000);
          return;
        }
      }
      release();
      setState("idle");
      return;
    }

    const chunks = intoSpeakableChunks(text, status.max_speak_characters);
    if (chunks.length === 0) {
      setState("idle");
      return;
    }

    try {
      for (const chunk of chunks) {
        if (stopped.current) break;
        const blob = await speak(chunk, language);
        if (stopped.current) break;

        release();
        url.current = URL.createObjectURL(blob);
        const element = new Audio(url.current);
        audio.current = element;
        setState("playing");
        await new Promise<void>((resolve, reject) => {
          element.onended = () => resolve();
          element.onerror = () => reject(new Error("playback failed"));
          void element.play().catch(reject);
        });
      }
    } catch (caught) {
      if (!stopped.current) {
        setState("failed");
        // Logged rather than shown: the control is an icon beside an answer, and a sentence of
        // failure text there would displace the answer. The title carries it.
        console.warn(
          "read-aloud failed:",
          caught instanceof ApiError ? caught.status : caught,
        );
        window.setTimeout(() => setState("idle"), 3000);
        return;
      }
    }
    release();
    setState("idle");
  }, [
    text,
    language,
    voice,
    playInBrowser,
    status.max_speak_characters,
    release,
  ]);

  function stop(): void {
    stopped.current = true;
    release();
    setState("idle");
  }

  // Absent, not disabled, where *neither* engine can read this language. A control that cannot
  // work is worse than no control: it invites a press and explains nothing.
  //
  // `voice === undefined` means the browser has not finished listing its voices. The control waits
  // rather than rendering and vanishing, unless the server can already do it.
  const serverCanRead =
    status.enabled &&
    (status.speaks.length === 0 || status.speaks.includes(language));
  if (!voice && !serverCanRead) return null;

  const busy = state === "loading" || state === "playing";
  const label =
    state === "failed"
      ? t("speech.readFailed")
      : busy
        ? t("speech.stopReading")
        : t("speech.readAloud");

  return (
    <button
      type="button"
      onClick={() => (busy ? stop() : void play())}
      title={label}
      aria-label={label}
      // So a screen reader announces the state rather than only the name changing under it.
      aria-pressed={busy}
      className={cx(
        ACTION_BUTTON,
        state === "failed" && "text-statusDanger",
        busy && "border-accentBorder bg-accentSubtle text-accentStrong",
        className,
      )}
    >
      {busy ? <SpeakerOffIcon /> : <SpeakerIcon />}
      <span role="status" className="sr-only">
        {label}
      </span>
    </button>
  );
}
